import { Box, Chip, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { useCurrentTenantInfo } from "@/common/hooks/entity/tenant/useCurrentTenantInfo";
import { AutocompleteOptionType } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { TenantDto, TenantGetPaginatedDto } from "@/core/api/generated/v1";

import { tenantToOption } from "./TenantAutocomplete";
import TenantModeChip from "./TenantModeChip";

export interface TenantsAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<TenantDto> {
  searchFilters?: Partial<Omit<TenantGetPaginatedDto, "search" | "includeIds">>;
}

export default function TenantsAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: TenantsAutocompleteProps) {
  const currentTenantInfo = useCurrentTenantInfo();

  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={tenantToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.tenantsApi.apiV1TenantsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          tenantGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          tenantGetPaginatedDto: {
            ...params.tenantGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Companies'
      placeholder='Search...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props} secondaryAction={<TenantModeChip mode={option.data?.mode} />}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='company' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}{" "}
                    {currentTenantInfo?.identifier === option.data?.identifier && (
                      <Chip size='extraSmall' variant='outlined' color='default' label='You' />
                    )}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>{option.data?.identifier}</Box>
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
